import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useStripe as useStripeContext } from '../contexts/StripeContext';
import { STRIPE_CONFIG } from '../config/stripe';
import { CreditCard, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { API_URLS } from '../constants/api';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      fontFamily: 'system-ui, sans-serif',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#9e2146'
    }
  },
  hidePostalCode: false, // Show zip code field
  classes: {
    base: 'w-full'
  }
};

const SubscriptionManager = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { updateSubscription, reloadSubscription, perpetualTrialMode } =
    useStripeContext();
  const stripe = useStripe();
  const elements = useElements();

  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const plans = [
    {
      id: 'monthly',
      name: 'Pro Plan',
      price: `$${STRIPE_CONFIG.plans.monthly.amount / 100}`,
      period: `per ${STRIPE_CONFIG.plans.monthly.interval}`,
      description: 'Unlimited searches and exports for professional use',
      features: [
        'Unlimited case searches',
        'Export case data',
        'Advanced analytics',
        'Priority support',
        'Full case database access'
      ],
      popular: false
    },
    {
      id: 'annual',
      name: 'Pro Plan',
      price: `$${STRIPE_CONFIG.plans.annual.amount / 100}`,
      period: `per ${STRIPE_CONFIG.plans.annual.interval}`,
      description: 'Unlimited searches and exports for professional use',
      features: [
        'Unlimited case searches',
        'Export case data',
        'Advanced analytics',
        'Priority support',
        'Full case database access',
        '2 months free'
      ],
      popular: true
    }
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !currentUser) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement),
          billing_details: {
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email
          }
        });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      // Create subscription with trial
      const response = await fetch(API_URLS.CREATE_SUBSCRIPTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerId: currentUser.uid,
          customerEmail: currentUser.email,
          customerName: currentUser.displayName || currentUser.email,
          priceId:
            selectedPlan === 'monthly'
              ? process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID
              : process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID,
          paymentMethodId: paymentMethod.id
        })
      });

      const { subscription, error: subError } = await response.json();

      console.log('Backend response:', { subscription, subError });

      if (subError) {
        setError(subError);
        setLoading(false);
        return;
      }

      if (!subscription) {
        setError('No subscription data received from server');
        setLoading(false);
        return;
      }

      // Update user's subscription data
      console.log('Calling updateSubscription with:', subscription);
      await updateSubscription(subscription);

      // Reload subscription to ensure state is updated
      await reloadSubscription();

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Subscription error:', err);
      setError('Failed to create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show coming soon message in perpetual trial mode
  if (perpetualTrialMode) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-auto">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Coming Soon!
          </h2>
          <p className="text-gray-600 mb-4">
            We&apos;re preparing Docket Genie for launch. Enjoy free access to
            all features while we get everything ready.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Subscriptions will be available soon. Stay tuned for updates!
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-auto">
        <div className="text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Subscription Created!
          </h2>
          <p className="text-gray-600 mb-4">
            Your Pro subscription is now active! You have unlimited access to
            all features.
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Continue to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Upgrade to Pro</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ×
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Upgrade to Pro for unlimited searches, exports, and notifications. Your
        subscription starts immediately.
      </p>

      {/* Plan Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
              selectedPlan === plan.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            } ${plan.popular ? 'relative' : ''}`}
            onClick={() => setSelectedPlan(plan.id)}
          >
            {plan.popular && (
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                  Most Popular
                </span>
              </div>
            )}
            <div className="text-center">
              <h3 className="font-medium text-gray-900 mb-1">{plan.name}</h3>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {plan.price}
              </div>
              <div className="text-sm text-gray-600 mb-2">{plan.period}</div>
              <p className="text-xs text-gray-500">{plan.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <CreditCard className="h-4 w-4 inline mr-2" />
            Payment Information
          </label>

          <div className="p-4 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Enter your card details including number, expiry, CVC, and zip code
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <CheckCircle className="h-4 w-4" />
            <span className="font-medium">Pro Plan Features:</span>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Unlimited case searches</li>
            <li>• Export case data and reports</li>
            <li>• Advanced analytics and insights</li>
            <li>• Full case database access</li>
            <li>• Priority customer support</li>
            <li>• Cancel anytime</li>
          </ul>
        </div>

        <button
          type="submit"
          disabled={!stripe || loading}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Creating Subscription...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Upgrade to Pro
            </>
          )}
        </button>
      </form>
    </div>
  );
};

SubscriptionManager.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default SubscriptionManager;
